<template>
  <div class="fin">
    <!-- Раздел только для руководителя логистики (решение Анны 02.11.2026). Решает сервер. -->
    <section v-if="access === false" class="fin-lock">
      <div class="fin-lock__coin" aria-hidden="true"><i></i></div>
      <h1>Финансы</h1>
      <p>Раздел доступен руководителю логистики.</p>
      <router-link to="/summary" class="fin-btn fin-btn--ghost">К сводке логиста</router-link>
    </section>

    <template v-else>
      <header class="fin-hero" :style="heroStyle" @pointermove="onHeroMove" @pointerleave="onHeroLeave">
        <div class="fin-hero__scene" aria-hidden="true">
          <span class="fin-hero__glow"></span>
          <span class="fin-hero__grid"></span>
          <span v-for="n in 6" :key="n" class="coin" :style="coinStyle(n)"><i></i></span>
        </div>

        <div class="fin-hero__text">
          <div class="fin-eyebrow">ALT Group · группа логистики</div>
          <h1>
            Финансы
            <KbHint article="team-goals" text="Как считаются показатели, план и гипотеза" />
          </h1>
          <p>{{ leadText }}</p>
          <div class="fin-month">
            <button type="button" aria-label="Предыдущий месяц" @click="go(-1)">‹</button>
            <b>{{ monthTitle(month) }}</b>
            <button type="button" aria-label="Следующий месяц" @click="go(1)">›</button>
            <button v-if="month !== thisMonth" type="button" class="fin-month__now" @click="setMonth(thisMonth)">
              текущий месяц
            </button>
          </div>
        </div>

        <div v-if="main" class="fin-gauge" :class="`is-${main.light}`">
          <svg viewBox="0 0 180 180" class="fin-gauge__svg">
            <defs>
              <linearGradient id="finGold" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stop-color="#ffe08a" />
                <stop offset="55%" stop-color="#f2b705" />
                <stop offset="100%" stop-color="#b8860b" />
              </linearGradient>
            </defs>
            <circle cx="90" cy="90" r="74" class="fin-gauge__track" />
            <circle cx="90" cy="90" r="74" class="fin-gauge__hyp" :style="ringStyle(main.forecast_pct, 74)" />
            <circle cx="90" cy="90" r="58" class="fin-gauge__track fin-gauge__track--in" />
            <circle cx="90" cy="90" r="58" class="fin-gauge__fact" :style="ringStyle(main.today_pct, 58)" />
          </svg>
          <div class="fin-gauge__center">
            <span class="fin-gauge__label">чистая комиссия</span>
            <b>{{ shownValue(main) }}</b>
            <span class="fin-gauge__pct">{{ pctText(main.today_pct) }} плана</span>
          </div>
          <div class="fin-gauge__legend">
            <span><i class="dot dot--fact"></i>факт к плану месяца</span>
            <span><i class="dot dot--hyp"></i>гипотеза: {{ formatGoalValue('rub', main.forecast) }}
              ({{ pctText(main.forecast_pct) }})</span>
          </div>
        </div>
      </header>

      <div class="fin-bar">
        <span v-if="data" :class="['fin-pill', `is-${data.overall}`]">
          <i class="fin-pill__pulse"></i>{{ lightLabel(data.overall) }}
        </span>
        <span class="fin-source">{{ sourceText(data && data.plan, month) }}</span>
        <span class="fin-bar__spacer"></span>
        <button type="button" class="fin-btn fin-btn--ghost" :disabled="pending" @click="reload">
          {{ pending ? 'Считаем…' : 'Обновить' }}
        </button>
        <button type="button" class="fin-btn" @click="openPlan">Изменить план месяца</button>
      </div>

      <p v-if="error" class="fin-error">Свежие данные не загрузились: {{ error }}.</p>

      <section v-if="!data && pending" class="fin-grid">
        <article v-for="n in 8" :key="n" class="fin-card fin-card--skeleton"></article>
      </section>

      <section v-if="data" class="fin-grid">
        <article
          v-for="(card, index) in cards"
          :key="card.key + month"
          :class="['fin-card', `is-${card.light}`, { 'is-pending': !card.available, 'is-click': isClickable(card) }]"
          :style="{ '--i': index }"
          @pointermove="onTilt"
          @pointerleave="onTiltEnd"
          @click="onCard(card)"
        >
          <div class="fin-card__inner">
            <div class="fin-card__top">
              <span class="fin-card__title">{{ card.title }}</span>
              <span v-if="card.available && card.light !== 'grey'" class="fin-card__light" :title="lightLabel(card.light)"></span>
            </div>
            <template v-if="card.available">
              <div class="fin-card__value">{{ shownValue(card) }}</div>
              <div class="fin-card__plan">{{ planText(card) }}</div>
              <div v-if="card.target > 0 && card.unit === 'rub'" class="fin-meter">
                <span class="fin-meter__hyp" :style="{ width: barModel(card).forecast + '%' }"></span>
                <span class="fin-meter__fact" :style="{ width: barModel(card).fact + '%' }"></span>
                <span
                  v-if="barModel(card).mark !== null"
                  class="fin-meter__mark"
                  :style="{ left: barModel(card).mark + '%' }"
                  title="Должно быть к сегодня"
                ></span>
              </div>
              <div v-if="card.forecast !== null && card.forecast !== undefined" class="fin-card__hyp">
                гипотеза: <b>{{ formatGoalValue(card.unit, card.forecast) }}</b>
                <span v-if="card.forecast_pct !== null && card.forecast_pct !== undefined"> · {{ pctText(card.forecast_pct) }} плана</span>
              </div>
              <div v-if="card.plan_to_date !== null && card.plan_to_date !== undefined" class="fin-card__note">
                к сегодня должно быть {{ formatGoalValue(card.unit, card.plan_to_date) }}
              </div>
              <div v-if="isClickable(card)" class="fin-card__more">
                {{ openKey === card.key ? 'Скрыть рейсы' : 'Показать рейсы' }} →
              </div>
            </template>
            <template v-else>
              <div class="fin-card__value fin-card__value--muted">скоро</div>
              <div class="fin-card__plan">{{ planText(card) }}</div>
              <div class="fin-card__note">Источник данных подключается.</div>
            </template>
            <p class="fin-card__hint" :title="card.hint">{{ card.hint }}</p>
          </div>
        </article>
      </section>

      <section v-if="data && openKey === 'negative_trips'" class="fin-panel">
        <h2>Минусовые рейсы месяца <span>итого {{ formatMoney(data.negative_sum) }}</span></h2>
        <div class="fin-table-wrap">
          <table class="fin-table">
            <thead><tr><th>Рейс</th><th>Логист</th><th>Маршрут</th><th>Клиент</th><th>Ставка клиента</th><th>Ставка перевозчика</th><th>Комиссия</th></tr></thead>
            <tbody>
              <tr v-for="t in data.negative_trips" :key="t.trip_number + t.stat_date">
                <td>
                  <a v-if="canTrip360 && t.trip_number" :href="`#trip360=${t.trip_number}`">{{ t.trip_number }}</a>
                  <span v-else>{{ t.trip_number || '—' }}</span>
                </td>
                <td>{{ t.logist || '—' }}</td>
                <td>{{ t.city_out || '—' }} → {{ t.city_in || '—' }}</td>
                <td>{{ t.client || '—' }}</td>
                <td>{{ formatMoney(t.customer_gross) }}</td>
                <td>{{ formatMoney(t.carrier_gross) }}</td>
                <td class="neg">{{ formatMoney(t.commission_rub) }}</td>
              </tr>
              <tr v-if="!data.negative_trips.length"><td colspan="7">Минусовых рейсов нет.</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section v-if="data && chart" class="fin-panel fin-chart">
        <div class="fin-panel__head">
          <h2>Как набираем месяц <span>накоплено к дню, этот месяц против прошлого</span></h2>
          <div class="fin-tabs">
            <button
              v-for="c in data.charts"
              :key="c.key"
              type="button"
              :class="['fin-tab', { 'is-on': chartKey === c.key }]"
              @click="chartKey = c.key"
            >{{ c.title }}</button>
          </div>
        </div>
        <svg :viewBox="`0 0 ${chart.width} ${chart.height}`" class="fin-chart__svg">
          <defs>
            <linearGradient id="finArea" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="rgba(242,183,5,.38)" />
              <stop offset="100%" stop-color="rgba(242,183,5,0)" />
            </linearGradient>
          </defs>
          <line v-if="chart.planY !== null" x1="0" :x2="chart.width" :y1="chart.planY" :y2="chart.planY" class="fin-chart__plan" />
          <path :key="'p' + chartKey + month" :d="chart.previous" pathLength="1" class="fin-chart__prev" />
          <path :key="'a' + chartKey + month" :d="chart.area" class="fin-chart__area" />
          <path :key="'c' + chartKey + month" :d="chart.current" pathLength="1" class="fin-chart__cur" />
          <circle v-if="chart.todayX !== null" :cx="chart.todayX" :cy="chart.todayY" r="5" class="fin-chart__dot" />
        </svg>
        <div class="fin-chart__legend">
          <span><i class="dot dot--hyp"></i>этот месяц</span>
          <span><i class="dot dot--prev"></i>прошлый месяц (в масштабе дней этого)</span>
          <span v-if="chart.planY !== null"><i class="dot dot--plan"></i>план месяца</span>
        </div>
      </section>

      <section v-if="data && data.by_logist && data.by_logist.length" class="fin-panel">
        <button class="fin-btn fin-btn--ghost" type="button" @click="showTeam = !showTeam">
          {{ showTeam ? 'Скрыть разбивку по логистам' : 'Разбивка по логистам' }}
        </button>
        <transition name="fin-fade">
          <div v-if="showTeam" class="fin-table-wrap">
            <table class="fin-table">
              <thead><tr><th>Логист</th><th>Закрыто</th><th>Чистая комиссия</th><th>Маржа</th><th>Дельта</th><th>Минусовые</th></tr></thead>
              <tbody>
                <tr v-for="l in data.by_logist" :key="l.logist">
                  <td>{{ l.logist }}</td>
                  <td>{{ l.closed }}</td>
                  <td>{{ formatMoney(l.net_commission) }}</td>
                  <td>{{ formatGoalValue('pct', l.margin_pct) }}</td>
                  <td :class="{ neg: l.delta < 0 }">{{ formatMoney(l.delta) }}</td>
                  <td :class="{ neg: l.negative_trips > 0 }">{{ l.negative_trips }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition>
      </section>

      <!-- Внизу: выполнение на сегодня и гипотеза по месяцу (поручение Анны 02.11.2026). -->
      <section v-if="today" class="fin-today">
        <div class="fin-today__head">
          <div>
            <div class="fin-eyebrow">На сегодня</div>
            <h2>День {{ today.day }} из {{ today.days_total }} · прошло {{ pctText(today.calendar_pct) }} месяца</h2>
            <p v-if="today.prev_share_pct !== null">
              К этому дню {{ monthGenitive(today.prev_month) }} группа набрала {{ pctText(today.prev_share_pct) }}
              чистой комиссии месяца — по этому распределению и считается гипотеза.
            </p>
            <p v-else>Прошлый месяц не даёт образца — гипотеза пока по темпу текущего месяца.</p>
          </div>
        </div>
        <div class="fin-today__rows">
          <div class="fin-trow fin-trow--head">
            <span>Показатель</span><span>План месяца</span><span>К сегодня</span><span>Факт</span>
            <span>Выполнение</span><span>К графику</span><span>Гипотеза на месяц</span>
          </div>
          <div
            v-for="(row, index) in today.rows"
            :key="row.key + month"
            class="fin-trow"
            :class="`is-${todayStatus(row).tone}`"
            :style="{ '--i': index }"
          >
            <span class="fin-trow__title">{{ row.title }}</span>
            <span data-label="План месяца">{{ isEmpty(row.target) ? '—' : formatGoalValue(row.unit, row.target) }}</span>
            <span data-label="К сегодня">{{ isEmpty(row.plan_to_date) ? '—' : formatGoalValue(row.unit, row.plan_to_date) }}</span>
            <span data-label="Факт"><b>{{ formatGoalValue(row.unit, row.value) }}</b></span>
            <span data-label="Выполнение" class="fin-trow__pct">
              <i class="fin-mini"><i :style="{ width: Math.min(100, row.today_pct || 0) + '%' }"></i></i>
              {{ pctText(row.today_pct) }}
            </span>
            <span data-label="К графику" class="fin-trow__status">{{ todayStatus(row).text }}</span>
            <span data-label="Гипотеза">
              {{ isEmpty(row.forecast) ? '—' : formatGoalValue(row.unit, row.forecast) }}
              <em v-if="!isEmpty(row.forecast_pct)">{{ pctText(row.forecast_pct) }}</em>
            </span>
          </div>
        </div>
        <p class="fin-today__note">
          Выполнение — факт к плану месяца. «К сегодня» — сколько должно быть к этому дню, если месяц пойдёт
          как прошлый. Гипотеза — {{ methodText(main && main.forecast_method) || 'по распределению прошлого месяца' }}.
        </p>
      </section>

      <!-- Шторка и уведомление — поверх всего экрана (вне объёмной сцены героя). -->
      <teleport to="body">
      <transition name="fin-drawer">
        <div v-if="planOpen" class="fin-drawer" @click.self="planOpen = false">
          <aside class="fin-drawer__panel" role="dialog" aria-label="План месяца">
            <header class="fin-drawer__head">
              <div>
                <div class="fin-eyebrow">План месяца</div>
                <h2>{{ monthTitle(month) }}</h2>
                <p>{{ planData ? sourceText(planData, month) : 'Загружаем план…' }}</p>
              </div>
              <button type="button" class="fin-x" aria-label="Закрыть" @click="planOpen = false">×</button>
            </header>
            <form v-if="planData" class="fin-form" @submit.prevent="savePlan">
              <label v-for="goal in planData.goals" :key="goal.key" class="fin-field" :class="{ 'is-error': planErrors[goal.key] }">
                <span class="fin-field__title">{{ goal.title }}</span>
                <span class="fin-field__input">
                  <input
                    v-model="drafts[goal.key]"
                    :inputmode="goal.unit === 'rub' ? 'numeric' : 'decimal'"
                    :placeholder="isEmpty(goal.default) ? 'не задан' : planInputText(goal.default, goal.unit)"
                  />
                  <em>{{ goal.unit === 'rub' ? '₽' : goal.unit === 'pct' ? '%' : 'шт.' }}</em>
                </span>
                <span class="fin-field__hint">
                  {{ planErrors[goal.key] || (goal.direction === 'lower' ? 'не больше этого значения · ' : '') +
                    'по умолчанию: ' + (isEmpty(goal.default) ? 'не задан' : formatGoalValue(goal.unit, goal.default)) +
                    (goal.unit === 'rub' ? ' · можно «7,5 млн»' : '') + ' · пусто — план не задан' }}
                </span>
              </label>
              <label class="fin-field">
                <span class="fin-field__title">Почему меняете план</span>
                <textarea v-model="planComment" rows="2" placeholder="Например: план на ноябрь утверждён на планёрке"></textarea>
              </label>
              <p v-if="planError" class="fin-error">{{ planError }}</p>
              <div class="fin-form__actions">
                <button type="submit" class="fin-btn" :disabled="saving">{{ saving ? 'Сохраняем…' : 'Сохранить план' }}</button>
                <button type="button" class="fin-btn fin-btn--ghost" @click="planOpen = false">Отмена</button>
              </div>
            </form>
            <section v-if="planData && planData.history && planData.history.length" class="fin-history">
              <h3>История изменений</h3>
              <ul>
                <li v-for="(h, i) in planData.history" :key="i">
                  <b>{{ h.title }}</b>: {{ historyText(h, unitOf(h.key)) }}
                  <span>{{ formatDateTime(h.at) }} · {{ h.actor || '—' }}</span>
                  <em v-if="h.comment">«{{ h.comment }}»</em>
                </li>
              </ul>
            </section>
          </aside>
        </div>
      </transition>

      <transition name="fin-fade">
        <div v-if="toast" class="fin-toast">{{ toast }}</div>
      </transition>
      </teleport>
    </template>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import KbHint from '@/components/KbHint.vue'
import { fetchTeamGoalsAccess } from '@/backend/workspaceApi'
import { fetchFinance, fetchFinancePlan, saveFinancePlan } from '@/backend/financeApi'
import {
  barModel,
  buildPlanPayload,
  chartModel,
  currentMonth,
  formatDateTime,
  formatGoalValue,
  formatMoney,
  historyText,
  humanError,
  isEmpty,
  lightLabel,
  methodText,
  monthGenitive,
  monthTitle,
  pctText,
  planInputText,
  planText,
  shiftMonth,
  sourceText,
  todayStatus,
  tween,
} from './finance.mjs'

const REFRESH_MS = 10 * 60 * 1000
const COUNT_MS = 1100
const CHART_PLAN_KEY = { net_total: 'net_commission_total', revenue: 'revenue_total', expenses: 'expenses_total' }

const store = useStore()
const thisMonth = currentMonth()
const month = ref(thisMonth)
const access = ref(null)
const data = ref(null)
const pending = ref(false)
const error = ref('')
const openKey = ref('')
const showTeam = ref(false)
const chartKey = ref('net_total')
const shown = reactive({})
const ringOn = ref(false)
const hero = reactive({ x: 0, y: 0 })

const planOpen = ref(false)
const planData = ref(null)
const drafts = reactive({})
const planErrors = reactive({})
const planComment = ref('')
const planError = ref('')
const saving = ref(false)
const toast = ref('')

let timer = null
let frame = null
let toastTimer = null
const reduceMotion = typeof window !== 'undefined' && window.matchMedia
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches

const canTrip360 = computed(() => store.getters.hasPermission?.('trip360.view'))
const cards = computed(() => (data.value?.cards || []).filter((c) => c.key !== 'net_commission_total'))
const main = computed(() => (data.value?.cards || []).find((c) => c.key === 'net_commission_total') || null)
const today = computed(() => data.value?.today || null)
const leadText = computed(() => {
  if (!data.value) return pending.value ? 'Считаем показатели группы…' : 'План месяца, факт, выполнение на сегодня и гипотеза.'
  const p = data.value.period
  return `Прошло ${p.days_elapsed} из ${p.days_total} дн. Планы — на группу. Гипотеза на конец месяца — по объёму и распределению доходов и расходов прошлого месяца.`
})
const chart = computed(() => {
  const list = data.value?.charts || []
  const selected = list.find((c) => c.key === chartKey.value) || list[0]
  if (!selected) return null
  const planCard = (data.value?.cards || []).find((c) => c.key === CHART_PLAN_KEY[selected.key])
  return chartModel(selected, { width: 760, height: 240, pad: 20, plan: planCard ? planCard.target : null })
})
const heroStyle = computed(() => ({ '--hx': hero.x.toFixed(3), '--hy': hero.y.toFixed(3) }))

function coinStyle(n) {
  const spots = [[8, 22, 1.0], [22, 70, 0.7], [44, 14, 0.55], [63, 66, 0.85], [80, 24, 0.6], [92, 62, 0.45]]
  const [left, top, scale] = spots[(n - 1) % spots.length]
  return { left: `${left}%`, top: `${top}%`, '--s': scale, '--d': `${n * 0.7}s`, '--z': `${Math.round(scale * 80)}px` }
}

function ringStyle(pct, r) {
  const length = 2 * Math.PI * r
  const share = ringOn.value && !isEmpty(pct) ? Math.max(0, Math.min(100, pct)) / 100 : 0
  return { strokeDasharray: `${length}`, strokeDashoffset: `${length * (1 - share)}` }
}

function shownValue(card) {
  if (!card || isEmpty(card.value)) return '—'
  const value = card.key in shown ? shown[card.key] : card.value
  const rounded = card.unit === 'pct' ? Math.round(value * 10) / 10 : Math.round(value)
  return formatGoalValue(card.unit, rounded)
}

function animateNumbers() {
  if (frame) window.cancelAnimationFrame(frame)
  const list = (data.value?.cards || []).filter((c) => c.available && !isEmpty(c.value))
  if (reduceMotion) {
    list.forEach((c) => { shown[c.key] = c.value })
    ringOn.value = true
    return
  }
  ringOn.value = false
  const start = performance.now()
  const step = (now) => {
    const progress = (now - start) / COUNT_MS
    list.forEach((c) => { shown[c.key] = tween(0, c.value, progress) })
    if (progress < 1) frame = window.requestAnimationFrame(step)
    else frame = null
  }
  frame = window.requestAnimationFrame(step)
  window.setTimeout(() => { ringOn.value = true }, 60)
}

function isClickable(card) {
  return card.available && card.key === 'negative_trips' && card.value > 0
}

function onCard(card) {
  if (!isClickable(card)) return
  openKey.value = openKey.value === card.key ? '' : card.key
}

function onTilt(event) {
  if (reduceMotion) return
  const el = event.currentTarget
  const rect = el.getBoundingClientRect()
  const px = (event.clientX - rect.left) / rect.width - 0.5
  const py = (event.clientY - rect.top) / rect.height - 0.5
  el.style.setProperty('--ry', `${(px * 12).toFixed(2)}deg`)
  el.style.setProperty('--rx', `${(-py * 12).toFixed(2)}deg`)
  el.style.setProperty('--gx', `${((px + 0.5) * 100).toFixed(1)}%`)
  el.style.setProperty('--gy', `${((py + 0.5) * 100).toFixed(1)}%`)
}

function onTiltEnd(event) {
  const el = event.currentTarget
  el.style.setProperty('--rx', '0deg')
  el.style.setProperty('--ry', '0deg')
}

function onHeroMove(event) {
  if (reduceMotion) return
  const rect = event.currentTarget.getBoundingClientRect()
  hero.x = (event.clientX - rect.left) / rect.width - 0.5
  hero.y = (event.clientY - rect.top) / rect.height - 0.5
}

function onHeroLeave() {
  hero.x = 0
  hero.y = 0
}

function denied(e) {
  return e?.response?.status === 403
}

async function load() {
  pending.value = true
  try {
    data.value = await fetchFinance(month.value)
    error.value = ''
    animateNumbers()
  } catch (e) {
    if (denied(e)) access.value = false
    error.value = humanError(e)
  } finally {
    pending.value = false
  }
}

async function loadPlan() {
  try {
    planData.value = await fetchFinancePlan(month.value)
  } catch (e) {
    if (denied(e)) access.value = false
    planData.value = null
    planError.value = `План не загрузился: ${humanError(e)}`
  }
}

function reload() {
  load()
}

function setMonth(value) {
  month.value = value
}

function go(delta) {
  setMonth(shiftMonth(month.value, delta))
}

function fillDrafts() {
  Object.keys(drafts).forEach((key) => { delete drafts[key] })
  Object.keys(planErrors).forEach((key) => { delete planErrors[key] })
  for (const goal of planData.value?.goals || []) drafts[goal.key] = planInputText(goal.target, goal.unit)
}

async function openPlan() {
  planOpen.value = true
  planError.value = ''
  planComment.value = ''
  await loadPlan()
  fillDrafts()
}

function unitOf(key) {
  return (planData.value?.goals || []).find((g) => g.key === key)?.unit || 'rub'
}

function showToast(text) {
  toast.value = text
  if (toastTimer) window.clearTimeout(toastTimer)
  toastTimer = window.setTimeout(() => { toast.value = '' }, 3200)
}

async function savePlan() {
  planError.value = ''
  Object.keys(planErrors).forEach((key) => { delete planErrors[key] })
  const result = buildPlanPayload(month.value, planData.value?.goals, drafts, planComment.value)
  if (result.errors) {
    Object.assign(planErrors, result.errors)
    planError.value = 'Проверьте отмеченные поля'
    return
  }
  if (result.error) {
    planError.value = result.error
    return
  }
  saving.value = true
  try {
    planData.value = await saveFinancePlan(result.payload)
    fillDrafts()
    planComment.value = ''
    planOpen.value = false
    showToast(`План ${monthGenitive(month.value)} сохранён`)
    await load()
  } catch (e) {
    planError.value = humanError(e)
  } finally {
    saving.value = false
  }
}

watch(month, () => {
  openKey.value = ''
  load()
  if (planOpen.value) openPlan()
})

onMounted(async () => {
  try {
    access.value = await fetchTeamGoalsAccess()
  } catch (e) {
    access.value = false
  }
  if (!access.value) return
  load()
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && !planOpen.value) load()
  }, REFRESH_MS)
})

onBeforeUnmount(() => {
  if (timer) window.clearInterval(timer)
  if (frame) window.cancelAnimationFrame(frame)
  if (toastTimer) window.clearTimeout(toastTimer)
})
</script>

<style scoped>
.fin { --gold: #f2b705; --gold-2: #ffe08a; --gold-3: #b8860b; --ink: #1f2430; --ink-2: #2b3142; --muted: #6b7280;
  --line: #e7e5df; --green: #16a34a; --yellow: #d97706; --red: #dc2626; --grey: #9ca3af;
  max-width: 1280px; margin: 0 auto; padding: 24px 20px 90px; color: var(--ink); display: flex; flex-direction: column;
  gap: 18px; }

/* ---------- hero ---------- */
.fin-hero { position: relative; overflow: hidden; display: grid; grid-template-columns: 1fr auto; gap: 24px;
  align-items: center; padding: 30px 32px; border-radius: 24px; color: #fff; perspective: 900px; isolation: isolate;
  background: radial-gradient(120% 140% at 0% 0%, #2b3142 0%, #1f2430 45%, #17191f 100%);
  box-shadow: 0 30px 60px -30px rgba(31, 36, 48, .55); }
.fin-hero__scene { position: absolute; inset: 0; transform-style: preserve-3d; pointer-events: none; z-index: -1;
  transform: rotateX(calc(var(--hy, 0) * -8deg)) rotateY(calc(var(--hx, 0) * 10deg)); transition: transform .4s ease-out; }
.fin-hero__glow { position: absolute; right: -8%; top: -60%; width: 560px; height: 560px; border-radius: 50%;
  background: radial-gradient(circle, rgba(242, 183, 5, .42), rgba(242, 183, 5, 0) 62%); animation: fin-breathe 8s ease-in-out infinite; }
.fin-hero__grid { position: absolute; inset: -40% -10% -10% -10%; opacity: .16;
  background-image: linear-gradient(rgba(255, 224, 138, .35) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 224, 138, .35) 1px, transparent 1px);
  background-size: 44px 44px; transform: rotateX(62deg) translateY(30%); transform-origin: bottom;
  mask-image: linear-gradient(to top, #000, transparent 75%); animation: fin-grid 14s linear infinite; }
.coin { position: absolute; width: 54px; height: 54px; transform-style: preserve-3d;
  transform: translateZ(var(--z)) scale(var(--s)); animation: fin-float 7s ease-in-out infinite; animation-delay: var(--d); }
.coin i { position: absolute; inset: 0; border-radius: 50%; transform-style: preserve-3d;
  background: radial-gradient(circle at 32% 30%, #fff4c4 0%, var(--gold-2) 22%, var(--gold) 55%, var(--gold-3) 100%);
  box-shadow: inset 0 0 0 3px rgba(255, 255, 255, .25), inset 0 -6px 10px rgba(120, 80, 0, .45), 0 10px 26px rgba(242, 183, 5, .35);
  animation: fin-spin 6s linear infinite; animation-delay: var(--d); }
.coin i::after { content: '₽'; position: absolute; inset: 0; display: grid; place-items: center; font-weight: 800;
  font-size: 24px; color: rgba(120, 80, 0, .55); text-shadow: 0 1px 0 rgba(255, 255, 255, .5); }
.fin-hero__text { position: relative; max-width: 640px; }
.fin-eyebrow { font-size: 11px; letter-spacing: .16em; text-transform: uppercase; color: var(--gold); font-weight: 600; }
.fin-hero h1 { margin: 6px 0 8px; font-size: 40px; letter-spacing: -.02em; display: flex; align-items: center; gap: 10px;
  background: linear-gradient(90deg, #fff 0%, #fff 40%, var(--gold-2) 50%, #fff 60%, #fff 100%); background-size: 250% 100%;
  -webkit-background-clip: text; background-clip: text; color: transparent; animation: fin-shine 6s ease-in-out infinite; }
.fin-hero p { margin: 0; color: #d6d8de; line-height: 1.55; max-width: 560px; }
.fin-month { display: inline-flex; align-items: center; gap: 6px; margin-top: 18px; padding: 6px; border-radius: 999px;
  background: rgba(255, 255, 255, .08); border: 1px solid rgba(255, 255, 255, .14); backdrop-filter: blur(8px); }
.fin-month b { min-width: 140px; text-align: center; font-size: 15px; }
.fin-month button { border: 0; width: 32px; height: 32px; border-radius: 50%; background: rgba(255, 255, 255, .1);
  color: #fff; font-size: 18px; cursor: pointer; transition: background .2s, transform .2s; }
.fin-month button:hover { background: var(--gold); color: var(--ink); transform: scale(1.08); }
.fin-month .fin-month__now { width: auto; padding: 0 12px; font-size: 12px; border-radius: 999px; }

/* ---------- gauge ---------- */
.fin-gauge { position: relative; width: 250px; display: flex; flex-direction: column; align-items: center; gap: 8px;
  transform: translateZ(40px) rotateY(calc(var(--hx, 0) * -10deg)) rotateX(calc(var(--hy, 0) * 10deg));
  transition: transform .4s ease-out; }
.fin-gauge__svg { width: 210px; height: 210px; transform: rotate(-90deg); filter: drop-shadow(0 12px 24px rgba(242, 183, 5, .25)); }
.fin-gauge__track { fill: none; stroke: rgba(255, 255, 255, .1); stroke-width: 12; }
.fin-gauge__track--in { stroke-width: 9; }
.fin-gauge__hyp { fill: none; stroke: url(#finGold); stroke-width: 12; stroke-linecap: round;
  transition: stroke-dashoffset 1.6s cubic-bezier(.2, .8, .2, 1); }
.fin-gauge__fact { fill: none; stroke: #fff; stroke-width: 9; stroke-linecap: round; opacity: .92;
  transition: stroke-dashoffset 1.3s cubic-bezier(.2, .8, .2, 1) .15s; }
.fin-gauge.is-red .fin-gauge__fact { stroke: #ff8a80; }
.fin-gauge.is-yellow .fin-gauge__fact { stroke: #ffd27a; }
.fin-gauge.is-green .fin-gauge__fact { stroke: #9ef0b5; }
.fin-gauge__center { position: absolute; top: 0; width: 210px; height: 210px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; text-align: center; }
.fin-gauge__center b { font-size: 22px; font-variant-numeric: tabular-nums; }
.fin-gauge__label { font-size: 11px; text-transform: uppercase; letter-spacing: .1em; color: #c9ccd4; }
.fin-gauge__pct { font-size: 13px; color: var(--gold-2); margin-top: 2px; }
.fin-gauge__legend { display: flex; flex-direction: column; gap: 2px; font-size: 12px; color: #c9ccd4; text-align: center; }
.dot { display: inline-block; width: 8px; height: 8px; border-radius: 50%; margin-right: 6px; vertical-align: middle; }
.dot--fact { background: #fff; }
.dot--hyp { background: var(--gold); box-shadow: 0 0 8px var(--gold); }
.dot--prev { background: #9aa0ad; }
.dot--plan { background: transparent; border: 2px dashed var(--ink); width: 10px; height: 0; border-radius: 0; }

/* ---------- bar ---------- */
.fin-bar { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.fin-bar__spacer { flex: 1; }
.fin-source { font-size: 13px; color: var(--muted); }
.fin-pill { display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 700; padding: 6px 14px;
  border-radius: 999px; background: #f3f4f6; color: #4b5563; }
.fin-pill.is-green { background: #dcfce7; color: #166534; }
.fin-pill.is-yellow { background: #fef3c7; color: #92400e; }
.fin-pill.is-red { background: #fee2e2; color: #991b1b; }
.fin-pill__pulse { width: 8px; height: 8px; border-radius: 50%; background: currentColor; animation: fin-pulse 1.8s ease-out infinite; }
.fin-btn { border: 0; border-radius: 12px; padding: 10px 16px; font: inherit; font-size: 14px; font-weight: 650; cursor: pointer;
  color: var(--ink); background: linear-gradient(135deg, var(--gold-2), var(--gold) 55%, #e0a800);
  box-shadow: 0 8px 20px -8px rgba(242, 183, 5, .8), inset 0 1px 0 rgba(255, 255, 255, .6);
  transition: transform .2s, box-shadow .2s, filter .2s; text-decoration: none; display: inline-flex; align-items: center; }
.fin-btn:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 14px 26px -10px rgba(242, 183, 5, .9); }
.fin-btn:disabled { opacity: .6; cursor: default; }
.fin-btn--ghost { background: #fff; border: 1px solid var(--line); box-shadow: none; font-weight: 600; }
.fin-btn--ghost:hover:not(:disabled) { border-color: var(--gold); box-shadow: 0 6px 16px -10px rgba(31, 36, 48, .4); }
.fin-error { color: #b91c1c; font-size: 14px; margin: 0; }

/* ---------- cards (3D) ---------- */
.fin-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 14px; perspective: 1200px; }
.fin-card { --c: var(--grey); --rx: 0deg; --ry: 0deg; --gx: 50%; --gy: 0%; position: relative; border-radius: 18px;
  transform-style: preserve-3d; transform: rotateX(var(--rx)) rotateY(var(--ry));
  transition: transform .25s ease-out, box-shadow .25s; animation: fin-rise .8s cubic-bezier(.2, .8, .2, 1) backwards;
  animation-delay: calc(var(--i, 0) * 60ms); }
.fin-card::before { content: ''; position: absolute; inset: 0; border-radius: 18px; padding: 1px;
  background: linear-gradient(160deg, var(--c), rgba(255, 255, 255, 0) 45%, rgba(242, 183, 5, .35));
  -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0); -webkit-mask-composite: xor;
  mask-composite: exclude; pointer-events: none; }
.fin-card:hover { box-shadow: 0 24px 40px -22px rgba(31, 36, 48, .45); }
.fin-card.is-green { --c: var(--green); }
.fin-card.is-yellow { --c: var(--yellow); }
.fin-card.is-red { --c: var(--red); }
.fin-card.is-click { cursor: pointer; }
.fin-card__inner { position: relative; height: 100%; padding: 16px 16px 14px; border-radius: 18px; overflow: hidden;
  background: radial-gradient(140% 90% at var(--gx) var(--gy), rgba(242, 183, 5, .10), rgba(255, 255, 255, 0) 55%), #fff;
  border: 1px solid var(--line); display: flex; flex-direction: column; gap: 6px; transform: translateZ(18px); }
.fin-card.is-pending .fin-card__inner { background: #fafaf8; }
.fin-card--skeleton { min-height: 170px; border-radius: 18px; background: linear-gradient(90deg, #f1f0ec 25%, #faf9f6 50%, #f1f0ec 75%);
  background-size: 200% 100%; animation: fin-skeleton 1.2s linear infinite; }
.fin-card__top { display: flex; justify-content: space-between; align-items: flex-start; gap: 8px; }
.fin-card__title { font-size: 13px; font-weight: 650; color: #374151; }
.fin-card__light { width: 10px; height: 10px; border-radius: 50%; flex: none; margin-top: 3px; background: var(--c);
  box-shadow: 0 0 0 4px color-mix(in srgb, var(--c) 18%, transparent); }
.fin-card__value { font-size: 26px; font-weight: 750; letter-spacing: -.01em; font-variant-numeric: tabular-nums;
  color: var(--ink); transform: translateZ(26px); }
.fin-card__value--muted { color: var(--grey); font-size: 18px; }
.fin-card__plan { font-size: 12px; color: var(--muted); }
.fin-card__hyp { font-size: 12.5px; color: #374151; }
.fin-card__hyp b { color: var(--ink); }
.fin-card__note { font-size: 12px; color: #8a7a3d; }
.fin-card__more { font-size: 12.5px; font-weight: 650; color: #8a6d00; }
.fin-card__hint { margin: auto 0 0; padding-top: 6px; font-size: 11.5px; color: #9ca3af; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.fin-meter { position: relative; height: 8px; border-radius: 999px; background: #f1f0ec; overflow: visible; margin: 4px 0 2px; }
.fin-meter__hyp, .fin-meter__fact { position: absolute; left: 0; top: 0; bottom: 0; border-radius: 999px;
  animation: fin-grow 1.4s cubic-bezier(.2, .8, .2, 1) backwards; transform-origin: left; }
.fin-meter__hyp { background: repeating-linear-gradient(45deg, rgba(242, 183, 5, .35) 0 6px, rgba(242, 183, 5, .18) 6px 12px); }
.fin-meter__fact { background: linear-gradient(90deg, var(--gold-3), var(--gold), var(--gold-2)); box-shadow: 0 0 10px rgba(242, 183, 5, .45); }
.fin-meter__mark { position: absolute; top: -4px; width: 2px; height: 16px; background: var(--ink); border-radius: 2px; }
.fin-meter__mark::after { content: ''; position: absolute; top: -4px; left: -3px; border: 4px solid transparent; border-top-color: var(--ink); }

/* ---------- panels ---------- */
.fin-panel { background: #fff; border: 1px solid var(--line); border-radius: 20px; padding: 18px; animation: fin-rise .8s backwards; }
.fin-panel h2 { margin: 0 0 10px; font-size: 16px; }
.fin-panel h2 span { font-weight: 400; color: var(--muted); font-size: 13px; margin-left: 6px; }
.fin-panel__head { display: flex; justify-content: space-between; align-items: center; gap: 10px; flex-wrap: wrap; }
.fin-tabs { display: flex; gap: 6px; }
.fin-tab { border: 1px solid var(--line); background: #fff; border-radius: 999px; padding: 6px 12px; font: inherit; font-size: 12.5px;
  cursor: pointer; transition: all .2s; }
.fin-tab:hover { border-color: var(--gold); }
.fin-tab.is-on { background: var(--ink); color: #fff; border-color: var(--ink); }
.fin-chart__svg { width: 100%; height: auto; display: block; margin-top: 6px; overflow: visible; }
.fin-chart__prev { fill: none; stroke: #9aa0ad; stroke-width: 2; stroke-dasharray: .012 .01; opacity: .9; }
.fin-chart__cur { fill: none; stroke: var(--gold); stroke-width: 3.5; stroke-linecap: round; stroke-linejoin: round;
  stroke-dasharray: 1; stroke-dashoffset: 1; animation: fin-draw 1.6s cubic-bezier(.2, .8, .2, 1) forwards;
  filter: drop-shadow(0 6px 10px rgba(242, 183, 5, .4)); }
.fin-chart__area { fill: url(#finArea); opacity: 0; animation: fin-fade-in 1s .6s forwards; }
.fin-chart__plan { stroke: var(--ink); stroke-width: 1.5; stroke-dasharray: 6 6; opacity: .55; }
.fin-chart__dot { fill: var(--gold); stroke: #fff; stroke-width: 2.5; animation: fin-dot 2s ease-in-out infinite; transform-box: fill-box;
  transform-origin: center; }
.fin-chart__legend { display: flex; gap: 16px; flex-wrap: wrap; font-size: 12px; color: var(--muted); margin-top: 6px; }
.fin-table-wrap { overflow-x: auto; margin-top: 10px; }
.fin-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.fin-table th { text-align: left; font-weight: 600; color: var(--muted); padding: 8px; border-bottom: 1px solid var(--line); white-space: nowrap; }
.fin-table td { padding: 8px; border-bottom: 1px solid #f3f2ee; white-space: nowrap; }
.fin-table tr:hover td { background: #fffbeb; }
.neg { color: var(--red); font-weight: 600; }

/* ---------- today ---------- */
.fin-today { position: relative; overflow: hidden; border-radius: 24px; padding: 22px; color: #fff;
  background: linear-gradient(135deg, #1f2430 0%, #262b38 60%, #3b3420 100%); box-shadow: 0 30px 60px -32px rgba(31, 36, 48, .6); }
.fin-today::after { content: ''; position: absolute; right: -120px; bottom: -160px; width: 420px; height: 420px; border-radius: 50%;
  background: radial-gradient(circle, rgba(242, 183, 5, .28), rgba(242, 183, 5, 0) 65%); pointer-events: none; }
.fin-today__head h2 { margin: 4px 0 6px; font-size: 22px; }
.fin-today__head p { margin: 0 0 14px; color: #c9ccd4; font-size: 13.5px; max-width: 760px; }
.fin-today__rows { position: relative; z-index: 1; display: flex; flex-direction: column; gap: 6px; }
.fin-trow { display: grid; grid-template-columns: 1.6fr 1fr 1fr 1fr 1.1fr 1.3fr 1.2fr; gap: 10px; align-items: center;
  padding: 10px 12px; border-radius: 12px; background: rgba(255, 255, 255, .05); border: 1px solid rgba(255, 255, 255, .07);
  font-size: 13px; font-variant-numeric: tabular-nums; animation: fin-slide .7s cubic-bezier(.2, .8, .2, 1) backwards;
  animation-delay: calc(var(--i, 0) * 50ms); transition: background .2s, transform .2s; }
.fin-trow:not(.fin-trow--head):hover { background: rgba(255, 255, 255, .09); transform: translateX(3px); }
.fin-trow--head { background: none; border: 0; color: #9aa0ad; font-size: 11.5px; text-transform: uppercase; letter-spacing: .06em; animation: none; }
.fin-trow__title { font-weight: 600; }
.fin-trow b { color: #fff; }
.fin-trow em { font-style: normal; color: var(--gold-2); margin-left: 6px; font-size: 12px; }
.fin-trow__pct { display: flex; align-items: center; gap: 8px; }
.fin-mini { position: relative; display: inline-block; width: 54px; height: 6px; border-radius: 999px; background: rgba(255, 255, 255, .12); overflow: hidden; }
.fin-mini i { position: absolute; inset: 0 auto 0 0; background: linear-gradient(90deg, var(--gold-3), var(--gold)); border-radius: 999px;
  animation: fin-grow 1.4s cubic-bezier(.2, .8, .2, 1) backwards; transform-origin: left; }
.fin-trow__status { font-weight: 600; }
.fin-trow.is-green .fin-trow__status { color: #9ef0b5; }
.fin-trow.is-yellow .fin-trow__status { color: #ffd27a; }
.fin-trow.is-red .fin-trow__status { color: #ff8a80; }
.fin-trow.is-grey .fin-trow__status { color: #9aa0ad; }
.fin-today__note { position: relative; z-index: 1; margin: 12px 0 0; font-size: 12px; color: #9aa0ad; }

/* ---------- drawer ---------- */
.fin-drawer, .fin-toast { --gold: #f2b705; --gold-2: #ffe08a; --gold-3: #b8860b; --ink: #1f2430; --muted: #6b7280;
  --line: #e7e5df; --red: #dc2626; color: var(--ink); }
.fin-drawer { position: fixed; inset: 0; z-index: 1000; background: rgba(23, 25, 31, .5); transform: translateZ(0);
  display: flex; justify-content: flex-end; }
.fin-drawer__panel { width: min(520px, 100%); height: 100%; overflow-y: auto; background: #fbfaf7; padding: 22px;
  box-shadow: -30px 0 60px -30px rgba(0, 0, 0, .5); display: flex; flex-direction: column; gap: 16px; }
.fin-drawer__head { display: flex; justify-content: space-between; gap: 12px; padding-bottom: 12px; border-bottom: 1px solid var(--line); }
.fin-drawer__head h2 { margin: 4px 0; font-size: 24px; }
.fin-drawer__head p { margin: 0; color: var(--muted); font-size: 13px; }
.fin-x { border: 0; background: #fff; width: 36px; height: 36px; border-radius: 50%; font-size: 22px; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .08); transition: transform .2s; }
.fin-x:hover { transform: rotate(90deg); }
.fin-form { display: flex; flex-direction: column; gap: 12px; }
.fin-field { display: flex; flex-direction: column; gap: 4px; }
.fin-field__title { font-size: 13px; font-weight: 650; }
.fin-field__input { display: flex; align-items: center; gap: 8px; height: 42px; padding: 0 12px; border-radius: 12px;
  background: #fff; border: 1px solid var(--line); transition: border-color .2s, box-shadow .2s; }
.fin-field__input:focus-within { border-color: var(--gold); box-shadow: 0 0 0 4px rgba(242, 183, 5, .18); }
.fin-field__input input { flex: 1; border: 0; outline: 0; font: inherit; font-size: 15px; background: transparent;
  font-variant-numeric: tabular-nums; }
.fin-field__input em { font-style: normal; color: var(--muted); }
.fin-field__hint { font-size: 11.5px; color: #8a7a3d; }
.fin-field.is-error .fin-field__input { border-color: var(--red); }
.fin-field.is-error .fin-field__hint { color: var(--red); }
.fin-field textarea { border-radius: 12px; border: 1px solid var(--line); padding: 10px 12px; font: inherit; resize: vertical; }
.fin-field textarea:focus { outline: 0; border-color: var(--gold); box-shadow: 0 0 0 4px rgba(242, 183, 5, .18); }
.fin-form__actions { display: flex; gap: 8px; }
.fin-history h3 { font-size: 14px; margin: 0 0 8px; }
.fin-history ul { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 8px; }
.fin-history li { font-size: 12.5px; padding: 8px 10px; border-radius: 10px; background: #fff; border: 1px solid var(--line);
  display: flex; flex-direction: column; gap: 2px; }
.fin-history li span { color: var(--muted); font-size: 11.5px; }
.fin-history li em { color: #8a7a3d; font-style: normal; }
.fin-toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%) translateZ(0); z-index: 1010; padding: 12px 18px;
  border-radius: 14px; background: var(--ink); color: #fff; font-weight: 600; box-shadow: 0 16px 40px -14px rgba(0, 0, 0, .55);
  border: 1px solid rgba(242, 183, 5, .5); }

/* ---------- lock ---------- */
.fin-lock { margin: 60px auto; max-width: 460px; text-align: center; display: flex; flex-direction: column; align-items: center;
  gap: 10px; perspective: 600px; }
.fin-lock h1 { margin: 10px 0 0; }
.fin-lock p { margin: 0 0 8px; color: var(--muted); }
.fin-lock__coin { position: relative; width: 90px; height: 90px; transform-style: preserve-3d; }
.fin-lock__coin i { position: absolute; inset: 0; border-radius: 50%;
  background: radial-gradient(circle at 32% 30%, #fff4c4, var(--gold) 55%, var(--gold-3)); animation: fin-spin 5s linear infinite; }

/* ---------- transitions ---------- */
.fin-drawer-enter-active, .fin-drawer-leave-active { transition: opacity .25s; }
.fin-drawer-enter-active .fin-drawer__panel, .fin-drawer-leave-active .fin-drawer__panel { transition: transform .35s cubic-bezier(.2, .8, .2, 1); }
.fin-drawer-enter-from, .fin-drawer-leave-to { opacity: 0; }
.fin-drawer-enter-from .fin-drawer__panel, .fin-drawer-leave-to .fin-drawer__panel { transform: translateX(100%); }
.fin-fade-enter-active, .fin-fade-leave-active { transition: opacity .25s, transform .25s; }
.fin-fade-enter-from, .fin-fade-leave-to { opacity: 0; transform: translateY(6px); }

@keyframes fin-rise { from { opacity: 0; transform: translateY(26px) rotateX(18deg) translateZ(-40px); } to { opacity: 1; } }
@keyframes fin-slide { from { opacity: 0; transform: translateX(-18px); } to { opacity: 1; transform: none; } }
@keyframes fin-float { 0%, 100% { transform: translateZ(var(--z)) scale(var(--s)) translateY(0); }
  50% { transform: translateZ(var(--z)) scale(var(--s)) translateY(-16px); } }
@keyframes fin-spin { from { transform: rotateY(0); } to { transform: rotateY(360deg); } }
@keyframes fin-breathe { 0%, 100% { transform: scale(1); opacity: .9; } 50% { transform: scale(1.12); opacity: 1; } }
@keyframes fin-grid { from { background-position: 0 0; } to { background-position: 0 44px; } }
@keyframes fin-shine { 0%, 100% { background-position: 100% 0; } 50% { background-position: 0 0; } }
@keyframes fin-pulse { 0% { box-shadow: 0 0 0 0 currentColor; } 100% { box-shadow: 0 0 0 8px transparent; } }
@keyframes fin-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
@keyframes fin-draw { to { stroke-dashoffset: 0; } }
@keyframes fin-fade-in { to { opacity: 1; } }
@keyframes fin-dot { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.35); } }
@keyframes fin-skeleton { from { background-position: 200% 0; } to { background-position: -200% 0; } }

@media (max-width: 980px) {
  .fin-hero { grid-template-columns: 1fr; }
  .fin-gauge { margin: 0 auto; }
  .fin-trow { grid-template-columns: 1fr 1fr; }
  .fin-trow--head { display: none; }
  .fin-trow__title { grid-column: 1 / -1; }
  .fin-trow span[data-label]::before { content: attr(data-label); display: block; font-size: 10.5px; color: #9aa0ad;
    text-transform: uppercase; letter-spacing: .05em; }
}
@media (max-width: 560px) {
  .fin { padding: 16px 12px 80px; }
  .fin-hero { padding: 22px 18px; }
  .fin-hero h1 { font-size: 30px; }
}
@media (prefers-reduced-motion: reduce) {
  .fin *, .fin *::before, .fin *::after { animation: none !important; transition: none !important; }
  .fin-chart__cur { stroke-dashoffset: 0; }
  .fin-chart__area { opacity: 1; }
}
</style>
